import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import {
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import GetAppIcon from '@material-ui/icons/GetApp'
import { useIntl } from 'react-intl'
import moment from 'moment'

import { organizationActions } from '../../_state/actions'
import { i18nMessages, i18nMessagesByKey } from '../../Logic/CodeAccessible.i18n.Messages'
import { CapeeshColors } from '../../assets/ColorPalette'
import CourseStatsUserResultTable from './CourseStatsUserResultTable'
import { CourseStatsDateRange, isCustomRangeInvalid, resolveDateRange } from './CourseStatsScopeBar'
import {
    CourseStatFilterCombinator,
    CourseStatFilterParameterType,
    CourseStatFilterPreset,
    CourseStatFilterScope,
    CourseStatMetric,
    CourseStatMetricValueType,
    CourseStatOperator,
    CourseStatResultGranularity,
    CourseStatResultSortColumn,
    compareCourseStatResultEntries,
    courseStatMetricValueTypes,
    isBooleanMetric,
    operatorTakesSecondValue,
    operatorTakesValue,
    operatorsForMetric,
    organizationAllowsIndividualUserStatistics,
} from '../../Views/Private/CourseStatisticsConstants'

const useStyles = makeStyles(() => ({
    root: {
        paddingBottom: '60px',
    },
    // The controls stay put while results scroll - most of the reason this became its own page.
    stickyHeader: {
        position: 'sticky',
        top: '0px',
        zIndex: 5,
        backgroundColor: 'white',
        paddingTop: '16px',
        paddingBottom: '16px',
        borderBottom: `1px solid ${CapeeshColors.Gray4}`,
    },
    title: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '20px',
    },
    intro: {
        fontFamily: 'Rubik',
        fontSize: '13px',
        color: CapeeshColors.Gray1,
        marginTop: '4px',
    },
    scopeRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        gap: '16px',
        marginTop: '16px',
    },
    scopeControl: {
        minWidth: '220px',
    },
    dateControl: {
        width: '170px',
    },
    presetControl: {
        minWidth: '260px',
    },
    parameterControl: {
        width: '160px',
    },
    ruleRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        gap: '12px',
        marginTop: '12px',
    },
    ruleControl: {
        minWidth: '170px',
    },
    valueControl: {
        width: '120px',
    },
    actionRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        marginTop: '16px',
    },
    resultHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        marginTop: '20px',
    },
    matchCount: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '14px',
    },
    note: {
        fontFamily: 'Rubik',
        fontSize: '12px',
        color: CapeeshColors.Gray1,
        marginTop: '8px',
    },
}))

const metricMessageKeys = {
    [CourseStatMetric.AverageScoreNormalized]: 'courseStatisticsMetricAverageScore',
    [CourseStatMetric.MinScoreNormalized]: 'courseStatisticsMetricMinScore',
    [CourseStatMetric.MaxScoreNormalized]: 'courseStatisticsMetricMaxScore',
    [CourseStatMetric.AverageStars]: 'courseStatisticsMetricAverageStars',
    [CourseStatMetric.LessonsPassedCount]: 'courseStatisticsMetricLessonsPassedCount',
    [CourseStatMetric.LessonsPassedFraction]: 'courseStatisticsMetricLessonsPassedFraction',
    [CourseStatMetric.LessonsPlayedCount]: 'courseStatisticsMetricLessonsPlayedCount',
    [CourseStatMetric.IsFinished]: 'courseStatisticsMetricIsFinished',
    [CourseStatMetric.AllLessonsPassed]: 'courseStatisticsMetricAllLessonsPassed',
    [CourseStatMetric.CompletionFraction]: 'courseStatisticsMetricCompletionFraction',
    [CourseStatMetric.HasStarted]: 'courseStatisticsMetricHasStarted',
    [CourseStatMetric.TimeSpentMinutes]: 'courseStatisticsMetricTimeSpentMinutes',
    [CourseStatMetric.DaysSinceLastActivity]: 'courseStatisticsMetricDaysSinceLastActivity',
}

const operatorMessageKeys = {
    [CourseStatOperator.LessThan]: 'courseStatisticsOperatorLessThan',
    [CourseStatOperator.LessThanOrEqual]: 'courseStatisticsOperatorLessThanOrEqual',
    [CourseStatOperator.GreaterThan]: 'courseStatisticsOperatorGreaterThan',
    [CourseStatOperator.GreaterThanOrEqual]: 'courseStatisticsOperatorGreaterThanOrEqual',
    [CourseStatOperator.Equal]: 'courseStatisticsOperatorEqual',
    [CourseStatOperator.NotEqual]: 'courseStatisticsOperatorNotEqual',
    [CourseStatOperator.Between]: 'courseStatisticsOperatorBetween',
    [CourseStatOperator.IsTrue]: 'courseStatisticsOperatorIsTrue',
    [CourseStatOperator.IsFalse]: 'courseStatisticsOperatorIsFalse',
}

// Scores travel as 0..1 but are entered as percentages. Converting in one place keeps the classic
// "60 versus 0.6" threshold bug out of the rule builder.
const toDisplayValue = (metric, value) => {
    if (value === null || value === undefined) {
        return ''
    }
    return courseStatMetricValueTypes[metric] === CourseStatMetricValueType.NormalizedPercentage
        ? Math.round(value * 100)
        : value
}

const toWireValue = (metric, displayValue) => {
    if (displayValue === '' || displayValue === null || displayValue === undefined) {
        return null
    }
    const parsed = Number(displayValue)
    if (Number.isNaN(parsed)) {
        return null
    }
    return courseStatMetricValueTypes[metric] === CourseStatMetricValueType.NormalizedPercentage
        ? parsed / 100
        : parsed
}

const defaultRule = () => ({
    scope: CourseStatFilterScope.Topic,
    metric: CourseStatMetric.AverageScoreNormalized,
    operator: CourseStatOperator.LessThan,
    value: 0.6,
    secondValue: null,
})

/**
 * "Find users" — the query builder and its results.
 *
 * Its own page rather than a panel under the course table: the controls have grown to a course
 * picker, a topic picker, a period, a preset, a rule builder and a sortable result table, and
 * scrolling past a summary strip to reach them, then losing them off the top while reading
 * results, was the wrong shape.
 *
 * Courses and topics come from the catalogue endpoint rather than the statistics overview. There
 * is no overview loaded on this page, and even on the statistics page the overview is narrowed to
 * whatever the scope bar selected, which would silently limit what could be searched.
 */
const CourseFindUsersContent = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const { organizationId, userGroupId, initialCourseGroupId, initialTopicId } = props

    const [courseGroupId, setCourseGroupId] = useState(initialCourseGroupId || null)
    const [topicId, setTopicId] = useState(initialTopicId || null)
    const [dateRange, setDateRange] = useState(CourseStatsDateRange.All)
    const [customFromDate, setCustomFromDate] = useState('')
    const [customToDate, setCustomToDate] = useState('')

    const [selectedPreset, setSelectedPreset] = useState('')
    const [presetParameters, setPresetParameters] = useState({})
    const [rules, setRules] = useState([defaultRule()])
    const [combinator, setCombinator] = useState(CourseStatFilterCombinator.And)

    const [sortBy, setSortBy] = useState(CourseStatResultSortColumn.AverageScore)
    const [sortDescending, setSortDescending] = useState(false)

    // The request that produced what is on screen. The export sends this rather than the current
    // form state, so editing a rule without pressing Apply does not change the file.
    const [appliedRequest, setAppliedRequest] = useState(null)

    const { fromDate, toDate } = resolveDateRange(dateRange, customFromDate, customToDate)
    const customRangeInvalid = isCustomRangeInvalid(dateRange, customFromDate, customToDate)

    const catalogue = props.organization.courseStatsCatalogue
    const presets = props.organization.courseStatsFilterPresets || []
    const result = props.organization.courseStatsFilterResult
    const loading = props.organization.courseStatsFilterLoading
    const exporting = props.organization.courseStatsExporting

    // A named list of learners with their scores. Only school mode organizations permit that.
    // Checked here as well as in the endpoints, because this url can be typed or bookmarked - the
    // buttons that lead here are already hidden elsewhere.
    //
    // Evaluated on render rather than in the route gate on purpose: myOrganizations is not loaded
    // when PrivateLayout runs its constructor on a hard refresh, so a route level check would have
    // to guess. The endpoints are the real boundary; this only decides what is shown.
    const currentOrganizationDto = props.organization.myOrganizations?.find((x) => x.id === organizationId)
    const organizationsLoaded = (props.organization.myOrganizations?.length || 0) > 0
    const allowed = organizationAllowsIndividualUserStatistics(currentOrganizationDto)

    useEffect(() => {
        // Entering the page is a fresh start. The store is persisted, so a result and its spinner
        // survive both navigation and a reload; without this the page can open showing someone
        // else's answer, or an Apply button spinning with no request behind it.
        props.dispatch(organizationActions.clearCourseStatsFilterResult())
    }, [])

    useEffect(() => {
        if (!organizationId || !allowed) {
            return
        }
        props.dispatch(organizationActions.getCourseStatsCatalogue(organizationId, { userGroupId }))
        props.dispatch(organizationActions.getCourseStatsFilterPresets(organizationId))
    }, [organizationId, userGroupId, allowed])

    const courses = catalogue?.courses || []
    const selectedCourse = useMemo(
        () => courses.find((course) => course.courseGroupId === courseGroupId) || null,
        [courses, courseGroupId]
    )
    const topics = selectedCourse?.topics || []

    const activePreset = useMemo(
        () => presets.find((preset) => preset.preset === selectedPreset),
        [presets, selectedPreset]
    )

    // StrugglingInCourse asks about one course's scores, so it is meaningless across all of them.
    // The backend has always said so through RequiresTopicSelection; nothing read it until now.
    const needsTopic = activePreset?.requiresTopicSelection === true && !topicId

    const translateKey = (key, fallback) => {
        const message = i18nMessagesByKey[key]
        return message ? intl.formatMessage(message) : fallback || key
    }

    const applyPreset = (presetValue) => {
        setSelectedPreset(presetValue)

        const preset = presets.find((candidate) => candidate.preset === presetValue)
        if (!preset) {
            return
        }

        const parameters = {}
        ;(preset.parameters || []).forEach((parameter) => {
            parameters[parameter.key] = parameter.defaultValue
        })
        setPresetParameters(parameters)

        if (preset.defaultFilter) {
            setCombinator(preset.defaultFilter.combinator)
            setRules(
                (preset.defaultFilter.rules || []).map((rule) => ({
                    scope: rule.scope,
                    metric: rule.metric,
                    operator: rule.operator,
                    value: rule.value,
                    secondValue: rule.secondValue,
                }))
            )
        }
    }

    // Nudging a preset parameter has to move the rule it drives, otherwise the builder would show
    // 60% while the query ran at 70%.
    const updatePresetParameter = (parameter, rawValue) => {
        const parsed = Number(rawValue)
        if (Number.isNaN(parsed)) {
            return
        }

        const wireValue =
            parameter.parameterType === CourseStatFilterParameterType.NormalizedPercentage ? parsed / 100 : parsed

        setPresetParameters({ ...presetParameters, [parameter.key]: wireValue })

        setRules(
            rules.map((rule) => {
                const isScoreRule =
                    parameter.parameterType === CourseStatFilterParameterType.NormalizedPercentage &&
                    courseStatMetricValueTypes[rule.metric] === CourseStatMetricValueType.NormalizedPercentage
                const isDaysRule =
                    parameter.parameterType === CourseStatFilterParameterType.Days &&
                    rule.metric === CourseStatMetric.DaysSinceLastActivity

                return isScoreRule || isDaysRule ? { ...rule, value: wireValue } : rule
            })
        )
    }

    const updateRule = (index, changes) => {
        setRules(
            rules.map((rule, ruleIndex) => {
                if (ruleIndex !== index) {
                    return rule
                }

                const updated = { ...rule, ...changes }

                // Switching to a boolean metric leaves a stale numeric operator behind.
                if (changes.metric !== undefined) {
                    const allowed = operatorsForMetric(updated.metric)
                    if (!allowed.includes(updated.operator)) {
                        updated.operator = allowed[0]
                    }
                    if (isBooleanMetric(updated.metric)) {
                        updated.value = null
                        updated.secondValue = null
                    }
                }

                return updated
            })
        )

        setSelectedPreset('')
    }

    const removeRule = (index) => {
        setRules(rules.filter((rule, ruleIndex) => ruleIndex !== index))
        setSelectedPreset('')
    }

    // A topic belongs to the course that was selected when it was chosen.
    const handleCourseChange = (value) => {
        setCourseGroupId(value === 'all' ? null : value)
        setTopicId(null)
    }

    const buildRequest = () => {
        const usableRules = rules.filter((rule) => {
            if (isBooleanMetric(rule.metric)) {
                return true
            }
            return rule.value !== null && rule.value !== undefined
        })

        if (usableRules.length === 0) {
            return null
        }

        return {
            courseGroupIds: courseGroupId ? [courseGroupId] : [],
            topicIds: topicId ? [topicId] : [],
            userGroupIds: [],
            fromDate,
            toDate,
            ignoreLicences: false,
            treatUnplayedAsZero: false,
            granularity: CourseStatResultGranularity.UserPerTopic,
            sortBy,
            sortDescending,
            filter: {
                combinator,
                rules: usableRules,
                groups: [],
            },
        }
    }

    const handleApply = () => {
        const request = buildRequest()
        if (!request || needsTopic || customRangeInvalid) {
            return
        }

        setAppliedRequest(request)
        props.dispatch(organizationActions.postCourseStatsFilter(organizationId, request, { userGroupId }))
    }

    const handleExport = () => {
        if (!appliedRequest) {
            return
        }

        // Re-sorting the table does change the file, because that is visible on screen. Editing a
        // rule without applying does not.
        const request = { ...appliedRequest, sortBy, sortDescending }

        const scopeName = props.scopeName || ''
        const topicName = topics.find((topic) => topic.topicId === topicId)?.name
        const namePieces = ['Course statistics', scopeName, topicName, moment().format('YYYY-MM-DD')].filter(
            (piece) => piece
        )

        props.dispatch(
            organizationActions.exportCourseStatsFilter(organizationId, request, {
                userGroupId,
                filename: `${namePieces.join(' ')}.xlsx`,
            })
        )
    }

    const handleSort = (column) => {
        if (column === sortBy) {
            setSortDescending(!sortDescending)
            return
        }
        setSortBy(column)
        // Last activity reads best most-recent-first; everything else worst-or-first-alphabetical.
        setSortDescending(column === CourseStatResultSortColumn.LastActivity)
    }

    const sortedEntries = useMemo(() => {
        if (!result?.entries) {
            return []
        }
        return [...result.entries].sort((left, right) =>
            compareCourseStatResultEntries(left, right, sortBy, sortDescending)
        )
    }, [result, sortBy, sortDescending])

    useEffect(() => {
        if (presets.length > 0 && selectedPreset === '' && rules.length === 1) {
            const lowScoreButCompleted = presets.find(
                (preset) => preset.preset === CourseStatFilterPreset.LowScoreButCompleted
            )
            if (lowScoreButCompleted) {
                applyPreset(lowScoreButCompleted.preset)
            }
        }
    }, [presets])

    if (organizationsLoaded && !allowed) {
        return (
            <div className={classes.root}>
                <Typography className={classes.title}>
                    {intl.formatMessage(i18nMessages.courseStatisticsFilterTitle)}
                </Typography>
                <Typography className={classes.intro}>
                    {intl.formatMessage(i18nMessages.courseStatisticsFindUsersNotAvailable)}
                </Typography>
            </div>
        )
    }

    return (
        <div className={classes.root}>
            <div className={classes.stickyHeader}>
                <Typography className={classes.title}>
                    {intl.formatMessage(i18nMessages.courseStatisticsFilterTitle)}
                </Typography>
                <Typography className={classes.intro}>
                    {intl.formatMessage(i18nMessages.courseStatisticsFilterIntro)}
                </Typography>

                <div className={classes.scopeRow}>
                    <FormControl className={classes.scopeControl}>
                        <InputLabel>{intl.formatMessage(i18nMessages.courseStatisticsColumnCourse)}</InputLabel>
                        <Select
                            value={courseGroupId || 'all'}
                            onChange={(event) => handleCourseChange(event.target.value)}>
                            <MenuItem value="all">
                                {intl.formatMessage(i18nMessages.courseStatisticsAllCourses)}
                            </MenuItem>
                            {courses.map((course) => (
                                <MenuItem key={course.courseGroupId} value={course.courseGroupId}>
                                    {course.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Only once a course is chosen - there is no sensible topic list spanning
                        every course. */}
                    {courseGroupId && (
                        <FormControl className={classes.scopeControl}>
                            <InputLabel>{intl.formatMessage(i18nMessages.courseStatisticsColumnTopic)}</InputLabel>
                            <Select
                                value={topicId || 'all'}
                                onChange={(event) =>
                                    setTopicId(event.target.value === 'all' ? null : event.target.value)
                                }>
                                <MenuItem value="all">
                                    {intl.formatMessage(i18nMessages.courseStatisticsAllTopics)}
                                </MenuItem>
                                {topics.map((topic) => (
                                    <MenuItem key={topic.topicId} value={topic.topicId}>
                                        {topic.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl className={classes.scopeControl}>
                        <InputLabel>{intl.formatMessage(i18nMessages.courseStatisticsPeriod)}</InputLabel>
                        <Select value={dateRange} onChange={(event) => setDateRange(event.target.value)}>
                            <MenuItem value={CourseStatsDateRange.All}>
                                {intl.formatMessage(i18nMessages.courseStatisticsDateRangeAll)}
                            </MenuItem>
                            <MenuItem value={CourseStatsDateRange.Last30}>
                                {intl.formatMessage(i18nMessages.courseStatisticsDateRangeLast30)}
                            </MenuItem>
                            <MenuItem value={CourseStatsDateRange.Last90}>
                                {intl.formatMessage(i18nMessages.courseStatisticsDateRangeLast90)}
                            </MenuItem>
                            <MenuItem value={CourseStatsDateRange.LastYear}>
                                {intl.formatMessage(i18nMessages.courseStatisticsDateRangeLastYear)}
                            </MenuItem>
                            <MenuItem value={CourseStatsDateRange.Custom}>
                                {intl.formatMessage(i18nMessages.courseStatisticsDateRangeCustom)}
                            </MenuItem>
                        </Select>
                    </FormControl>

                    {dateRange === CourseStatsDateRange.Custom && (
                        <React.Fragment>
                            <TextField
                                className={classes.dateControl}
                                type="date"
                                label={intl.formatMessage(i18nMessages.courseStatisticsDateFrom)}
                                value={customFromDate}
                                onChange={(event) => setCustomFromDate(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                className={classes.dateControl}
                                type="date"
                                label={intl.formatMessage(i18nMessages.courseStatisticsDateTo)}
                                value={customToDate}
                                onChange={(event) => setCustomToDate(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                                error={customRangeInvalid}
                            />
                        </React.Fragment>
                    )}

                    <FormControl className={classes.presetControl}>
                        <InputLabel>{intl.formatMessage(i18nMessages.courseStatisticsFilterPreset)}</InputLabel>
                        <Select value={selectedPreset} onChange={(event) => applyPreset(event.target.value)}>
                            <MenuItem value="">
                                {intl.formatMessage(i18nMessages.courseStatisticsFilterCustom)}
                            </MenuItem>
                            {presets.map((preset) => (
                                <MenuItem key={preset.preset} value={preset.preset}>
                                    {translateKey(preset.nameKey)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {(activePreset?.parameters || []).map((parameter) => (
                        <TextField
                            key={parameter.key}
                            className={classes.parameterControl}
                            type="number"
                            label={translateKey(parameter.nameKey)}
                            value={
                                parameter.parameterType === CourseStatFilterParameterType.NormalizedPercentage
                                    ? Math.round((presetParameters[parameter.key] ?? parameter.defaultValue) * 100)
                                    : presetParameters[parameter.key] ?? parameter.defaultValue
                            }
                            onChange={(event) => updatePresetParameter(parameter, event.target.value)}
                        />
                    ))}
                </div>

                {activePreset && (
                    <Typography className={classes.note}>{translateKey(activePreset.descriptionKey)}</Typography>
                )}

                {rules.map((rule, index) => (
                    <div className={classes.ruleRow} key={`rule_${index}`}>
                        <FormControl className={classes.ruleControl}>
                            <InputLabel>{intl.formatMessage(i18nMessages.courseStatisticsFilterRuleScope)}</InputLabel>
                            <Select
                                value={rule.scope}
                                onChange={(event) => updateRule(index, { scope: event.target.value })}>
                                <MenuItem value={CourseStatFilterScope.Topic}>
                                    {intl.formatMessage(i18nMessages.courseStatisticsScopeTopic)}
                                </MenuItem>
                                <MenuItem value={CourseStatFilterScope.CourseGroup}>
                                    {intl.formatMessage(i18nMessages.courseStatisticsScopeCourse)}
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl className={classes.ruleControl}>
                            <InputLabel>
                                {intl.formatMessage(i18nMessages.courseStatisticsFilterRuleMetric)}
                            </InputLabel>
                            <Select
                                value={rule.metric}
                                onChange={(event) => updateRule(index, { metric: event.target.value })}>
                                {Object.keys(metricMessageKeys).map((metricKey) => (
                                    <MenuItem key={metricKey} value={Number(metricKey)}>
                                        {intl.formatMessage(i18nMessages[metricMessageKeys[metricKey]])}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl className={classes.ruleControl}>
                            <InputLabel>
                                {intl.formatMessage(i18nMessages.courseStatisticsFilterRuleCondition)}
                            </InputLabel>
                            <Select
                                value={rule.operator}
                                onChange={(event) => updateRule(index, { operator: event.target.value })}>
                                {operatorsForMetric(rule.metric).map((operatorValue) => (
                                    <MenuItem key={operatorValue} value={operatorValue}>
                                        {intl.formatMessage(i18nMessages[operatorMessageKeys[operatorValue]])}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {operatorTakesValue(rule.operator) && (
                            <TextField
                                className={classes.valueControl}
                                type="number"
                                value={toDisplayValue(rule.metric, rule.value)}
                                onChange={(event) =>
                                    updateRule(index, { value: toWireValue(rule.metric, event.target.value) })
                                }
                            />
                        )}

                        {operatorTakesSecondValue(rule.operator) && (
                            <TextField
                                className={classes.valueControl}
                                type="number"
                                value={toDisplayValue(rule.metric, rule.secondValue)}
                                onChange={(event) =>
                                    updateRule(index, { secondValue: toWireValue(rule.metric, event.target.value) })
                                }
                            />
                        )}

                        <IconButton size="small" onClick={() => removeRule(index)} disabled={rules.length === 1}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>
                ))}

                <div className={classes.actionRow}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        <Button
                            variant="text"
                            color="primary"
                            onClick={() => {
                                setRules([...rules, defaultRule()])
                                setSelectedPreset('')
                            }}>
                            {intl.formatMessage(i18nMessages.courseStatisticsFilterAddRule)}
                        </Button>

                        <RadioGroup
                            row
                            value={combinator}
                            onChange={(event) => setCombinator(Number(event.target.value))}>
                            <FormControlLabel
                                value={CourseStatFilterCombinator.And}
                                control={<Radio color="primary" size="small" />}
                                label={intl.formatMessage(i18nMessages.courseStatisticsFilterMatchAll)}
                            />
                            <FormControlLabel
                                value={CourseStatFilterCombinator.Or}
                                control={<Radio color="primary" size="small" />}
                                label={intl.formatMessage(i18nMessages.courseStatisticsFilterMatchAny)}
                            />
                        </RadioGroup>
                    </div>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApply}
                        disabled={loading || needsTopic || customRangeInvalid}>
                        {loading ? (
                            <CircularProgress size={22} color="inherit" />
                        ) : (
                            intl.formatMessage(i18nMessages.courseStatisticsFilterApply)
                        )}
                    </Button>
                </div>

                {needsTopic && (
                    <Typography className={classes.note}>
                        {intl.formatMessage(i18nMessages.courseStatisticsFilterNeedsTopic)}
                    </Typography>
                )}

                {customRangeInvalid && (
                    <Typography className={classes.note}>
                        {intl.formatMessage(i18nMessages.courseStatisticsDateRangeInvalid)}
                    </Typography>
                )}
            </div>

            {result && (
                <div>
                    <div className={classes.resultHeader}>
                        <div>
                            <Typography className={classes.matchCount}>
                                {intl.formatMessage(i18nMessages.courseStatisticsFilterMatchCount, {
                                    users: result.numberOfMatchingUsers,
                                    evaluated: result.numberOfUsersEvaluated,
                                })}
                            </Typography>
                            {result.userIdentitiesHidden && (
                                <Typography className={classes.note}>
                                    {intl.formatMessage(i18nMessages.courseStatisticsFilterIdentitiesHidden)}
                                </Typography>
                            )}
                        </div>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<GetAppIcon />}
                            disabled={exporting || !appliedRequest || result.numberOfMatchingUsers === 0}
                            onClick={handleExport}>
                            {exporting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                intl.formatMessage(i18nMessages.courseStatisticsExport)
                            )}
                        </Button>
                    </div>

                    {result.numberOfMatchingUsers === 0 ? (
                        <Typography className={classes.note}>
                            {intl.formatMessage(i18nMessages.courseStatisticsFilterNoMatches)}
                        </Typography>
                    ) : (
                        <CourseStatsUserResultTable
                            entries={sortedEntries}
                            sortBy={sortBy}
                            sortDescending={sortDescending}
                            onSort={handleSort}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

function mapStateToProps(state) {
    const { organization, user } = state
    return {
        organization,
        user,
    }
}

export default connect(mapStateToProps)(CourseFindUsersContent)
